/**
 * King Living override for useSplitPanelDoubleImageBlock hook
 * Uses 'full' variant by default to include primaryImage and contentCollection data
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseSplitPanelDoubleImageBlockById,
  getBaseSplitPanelDoubleImageBlocks,
  getFullSplitPanelDoubleImageBlockById,
  getFullSplitPanelDoubleImageBlocks,
  getSplitPanelDoubleImageBlockById,
  getSplitPanelDoubleImageBlocks,
} from '@amblique/contentful/app/contentful/queries';

export function useSplitPanelDoubleImageBlockById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full', // Default to 'full' to include image data
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseSplitPanelDoubleImageBlockById :
    variant === 'full' ?
      getFullSplitPanelDoubleImageBlockById :
      getSplitPanelDoubleImageBlockById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'splitPanelDoubleImageBlock',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService?.query(print(query), {
        ...variables,
        id,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.splitPanelDoubleImageBlock || null;
    },
    ...queryOptions,
  });
}

export function useSplitPanelDoubleImageBlocks(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full', // Default to 'full' to include image data
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query = variant === 'base' ?
    getBaseSplitPanelDoubleImageBlocks :
    variant === 'full' ?
      getFullSplitPanelDoubleImageBlocks :
      getSplitPanelDoubleImageBlocks;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'splitPanelDoubleImageBlock',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService?.query(print(query), {
        ...variables,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.splitPanelDoubleImageBlockCollection || {total: 0, skip: 0, limit: 0, items: []};
    },
    ...queryOptions,
  });
}
